import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export declare type OfficeConnectionType = 'None' | 'AWS' | 'API' | 'S7SmartTicketing' | 'S7ARDWeb';

@Injectable()
export class ReferencesService {
    private portalMenuUrl: string = 'api/menu';
    private referencesUrl: string = 'api/references';
    private agencyInfoUrl: string = 'api/agency-info';
    private activeAgencyRequestsUrl: string = 'api/active-agency-requests';

    constructor(private httpClient: HttpClient) {}

    public getPortalMenuForCurrentUser(): Observable<Backend.PortalMenuFirstLevelMenuItem[]> {
        return this.httpClient.get<Backend.PortalMenuFirstLevelMenuItem[]>(this.portalMenuUrl);
    }

    public getStates(searchTerm: string | null): Observable<Backend.StateInfo[]> {
        const params = new HttpParams({
            fromObject: {
                searchTerm: searchTerm || ''
            }
        });
        return this.httpClient.get<Backend.StateInfo[]>(`${this.referencesUrl}/states`, { params });
    }

    public getCountryByCity(cityCode: string): Observable<Backend.CountryInfo> {
        const params = new HttpParams({
            fromObject: {
                cityCode
            }
        });
        return this.httpClient.get<Backend.CountryInfo>(`${this.referencesUrl}/country-by-city`, { params });
    }

    public getCountries(searchTerm: string | null): Observable<Backend.CountryInfo[]> {
        const params = new HttpParams({
            fromObject: {
                searchTerm: searchTerm || ''
            }
        });
        return this.httpClient.get<Backend.CountryInfo[]>(`${this.referencesUrl}/countries`, { params });
    }

    public getCurrencies(): Observable<Backend.CurrencyInfo[]> {
        return this.httpClient.get<Backend.CurrencyInfo[]>(`${this.referencesUrl}/currency-list`);
    }

    public getCities(countryCode: string | null, iataOnly: boolean | null, searchTerm: string | null): Observable<Backend.CityInfo[]> {
        const params = new HttpParams({
            fromObject: {
                countryCode: countryCode || '',
                iataOnly: iataOnly == null ? '' : iataOnly ? 'true' : 'false',
                searchTerm: searchTerm || ''
            }
        });
        return this.httpClient.get<Backend.CityInfo[]>(`${this.referencesUrl}/cities`, { params });
    }

    public getBindedCity(cityCode: string): Observable<Backend.CityInfo> {
        const params = new HttpParams({
            fromObject: {
                cityCode
            }
        });
        return this.httpClient.get<Backend.CityInfo>(`${this.referencesUrl}/binded-city`, { params });
    }

    public getBindedCityByNameAndRegion(cityName: string, regionName: string, isResident: boolean, address: string, tin: string, vat: string): Observable<Backend.CityInfo> {
        const params = new HttpParams({
            fromObject: {
                cityName,
                regionName,
                isResident: isResident ? 'true' : 'false',
                address,
                tin,
                vat
            }
        });
        return this.httpClient.get<Backend.CityInfo>(`${this.referencesUrl}/binded-city-by-name`, { params });
    }

    public getAirports(cityCode: string | null, searchTerm: string | null): Observable<Backend.AirportInfo[]> {
        const params = new HttpParams({
            fromObject: {
                cityCode: cityCode || '',
                searchTerm: searchTerm || ''
            }
        });
        return this.httpClient.get<Backend.AirportInfo[]>(`${this.referencesUrl}/airports`, { params });
    }

    public getTaxpayerInfo(tin: string): Observable<Backend.TaxpayerInfo[] | null> {
        return this.httpClient.get<Backend.TaxpayerInfo[] | null>(`${this.referencesUrl}/taxpayer-info/${tin}`);
    }

    public getAgencyContacts(): Observable<Backend.AgencyContactInfo[]> {
        return this.httpClient.get<Backend.AgencyContactInfo[]>(`${this.agencyInfoUrl}/contacts`);
    }

    public getAgencyBankDetails(): Observable<Backend.BankAccountInfo[]> {
        return this.httpClient.get<Backend.BankAccountInfo[]>(`${this.agencyInfoUrl}/bank-details`);
    }

    public getAgencyOffices(connectionType: OfficeConnectionType, searchTerm: string | null): Observable<Backend.AgencyOfficeInfo[]> {
        const params = new HttpParams({
            fromObject: {
                connectionType,
                searchTerm: searchTerm || ''
            }
        });
        return this.httpClient.get<Backend.AgencyOfficeInfo[]>(`${this.agencyInfoUrl}/offices`, { params });
    }

    public getAgencyAgreements(): Observable<Backend.AgencyAgreementInfo[]> {
        return this.httpClient.get<Backend.AgencyAgreementInfo[]>(`${this.agencyInfoUrl}/agreements`);
    }

    public getAirportInfoByIataCode(iataCode: string | null): Observable<Backend.CityAirportInfo> {
        return this.httpClient.get<Backend.CityAirportInfo>(`${this.referencesUrl}/airports/${iataCode}`);
    }
    public getCityAirportList(iataCodes: string[] | null): Observable<Backend.CityAirportInfo[]> {
        let queryParams = '';
        if (iataCodes != null && iataCodes.length > 0) {
            queryParams += '?'
            const params = iataCodes.map((x: string) => `iataCodes=${x}`);
            queryParams += params.join('&');
        }
        return this.httpClient.get<Backend.CityAirportInfo[]>(`${this.referencesUrl}/city-airport-list${queryParams}`);
    }

    public getBPCodeOfRequest(requestId: string): Observable<Backend.BusinessProcessType> {
        return this.httpClient.get<Backend.BusinessProcessType>(`${this.referencesUrl}/bp-code-of-request/${requestId}`);
    }

    public getActiveAgencyRequests(businessProcessTypes: Backend.BusinessProcessType[]): Observable<Backend.RequestValidationInfo[]> {
        let queryParams = '';
        if (businessProcessTypes != null && businessProcessTypes.length > 0) {
            queryParams += '?'
            const params = businessProcessTypes.map((x: Backend.BusinessProcessType) => `businessProcessTypes=${x}`);

            queryParams += params.join('&');
        }
        return this.httpClient.get<Backend.RequestValidationInfo[]>(`${this.activeAgencyRequestsUrl}${queryParams}`);
    }
}
